import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyPropertyPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [imageGalleryProperty, setImageGalleryProperty] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [leadData, setLeadData] = useState({
    name: "",
    mobile: "",
    email: "",
    message: ""
  });

  React.useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/property-leads`, {
        ...leadData,
        property_id: selectedProperty.id
      });
      alert("Thank you! We'll contact you soon.");
      setSelectedProperty(null);
      setLeadData({ name: "", mobile: "", email: "", message: "" });
    } catch (error) {
      console.error("Lead submission error:", error);
      alert("Error submitting details. Please try again.");
    }
  };

  const openWhatsApp = (number, property) => {
    const message = `Hi, I'm interested in the ${property.bhk} property at ${property.society_name}, ${property.location}`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-700">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl font-semibold">Loading premium properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background Image */}
      <div 
        className="relative bg-gradient-to-r from-brand-800 to-brand-600 py-20 overflow-hidden"
        style={{
          backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.pexels.com/photos/7672058/pexels-photo-7672058.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg animate-fade-in" data-testid="buy-property-title">
            Buy Your Dream Property
          </h1>
          <p className="text-2xl text-white font-semibold drop-shadow-md animate-fade-in">
            Premium Properties in Ghaziabad, Indirapuram, Vaishali, Vasundhara & Noida
          </p>
          <div className="mt-8 flex justify-center space-x-8 text-white animate-fade-in">
            <div className="text-center">
              <div className="text-3xl font-bold">✓</div>
              <div className="text-sm">Verified Properties</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">✓</div>
              <div className="text-sm">Best Prices</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">✓</div>
              <div className="text-sm">Direct Owners</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {properties.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
            <div className="mb-4">
              <svg className="w-24 h-24 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-gray-600 text-xl font-semibold">No properties available at the moment</p>
            <p className="text-gray-500 mt-2">Check back soon for exciting new listings!</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Available Properties ({properties.length})
              </h2>
              <p className="text-gray-600">Handpicked premium properties for you</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property, index) => (
                <div 
                  key={property.id} 
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-fade-in" 
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid="property-card"
                >
                  {/* Property Image */}
                  <div 
                    className="relative h-56 bg-gradient-to-br from-brand-400 to-brand-500 overflow-hidden cursor-pointer group"
                    onClick={() => {
                      if (property.images && property.images.length > 0) {
                        setImageGalleryProperty(property);
                        setCurrentImageIndex(0);
                      }
                    }}
                  >
                    {property.images && property.images.length > 0 ? (
                      <>
                        <img
                          src={property.images[0]}
                          alt={property.society_name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        {property.images.length > 1 && (
                          <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm font-semibold">
                            📷 {property.images.length} Photos
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                          <span className="text-white text-lg font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            Click to view all images
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <svg className="w-20 h-20 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow-lg">
                      <span className="text-brand-600 font-bold text-sm">{property.bhk}</span>
                    </div>
                    {property.video_url && (
                      <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                        <span>VIDEO</span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{property.society_name}</h3>
                      <p className="text-gray-600 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {property.location}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <p className="text-gray-500 text-xs">Area</p>
                        <p className="font-semibold text-gray-900">{property.area_sqft} sq ft</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <p className="text-gray-500 text-xs">Floor</p>
                        <p className="font-semibold text-gray-900">{property.floor}</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-3xl font-bold text-brand-500">
                        ₹{property.price}L
                      </p>
                      <p className="text-sm text-gray-500">
                        ₹{Math.round(property.price * 100000 / property.area_sqft).toLocaleString()}/sq ft
                      </p>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {property.description}
                    </p>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedProperty(property)}
                        className="flex-1 bg-brand-500 text-white py-3 px-4 rounded-xl font-semibold hover:bg-brand-600 transition shadow-md hover:shadow-lg"
                        data-testid="contact-us-btn"
                      >
                        📞 Contact Us
                      </button>
                      <button
                        onClick={() => openWhatsApp(property.contact_whatsapp, property)}
                        className="bg-brand-500 text-white py-3 px-4 rounded-xl font-semibold hover:bg-brand-500 transition shadow-md hover:shadow-lg"
                        data-testid="whatsapp-btn"
                        title="WhatsApp"
                      >
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Contact Form Modal */}
        {selectedProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl transform transition-all animate-fade-in">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
                  <p className="text-gray-600 text-sm mt-1">{selectedProperty.society_name}, {selectedProperty.location}</p>
                  <p className="text-brand-500 font-bold text-lg mt-1">₹{selectedProperty.price}L</p>
                </div>
                <button
                  onClick={() => setSelectedProperty(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="property-lead-form">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                    placeholder="Your full name"
                    data-testid="property-lead-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile *</label>
                  <input
                    type="tel"
                    required
                    value={leadData.mobile}
                    onChange={(e) => setLeadData({ ...leadData, mobile: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                    placeholder="Your mobile number"
                    data-testid="property-lead-mobile"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                    placeholder="Your email address"
                    data-testid="property-lead-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message (Optional)</label>
                  <textarea
                    value={leadData.message}
                    onChange={(e) => setLeadData({ ...leadData, message: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                    rows="3"
                    placeholder="Any specific requirements..."
                    data-testid="property-lead-message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:bg-brand-600 transition shadow-lg hover:shadow-xl"
                  data-testid="submit-property-lead"
                >
                  Submit Inquiry
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Image Gallery Modal */}
        {imageGalleryProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
            <div className="w-full h-full flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 bg-black bg-opacity-80 p-4 relative">
                <div className="text-white text-center">
                  <h2 className="text-2xl font-bold">{imageGalleryProperty.society_name}</h2>
                  <p className="text-gray-300">{imageGalleryProperty.location} • {imageGalleryProperty.bhk} • ₹{imageGalleryProperty.price}L</p>
                </div>
                <button
                  onClick={() => {
                    setImageGalleryProperty(null);
                    setCurrentImageIndex(0);
                  }}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 text-4xl w-12 h-12 flex items-center justify-center"
                >
                  ✕
                </button>
              </div>

              {/* Main Content - Video and Images in Same Row */}
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="w-full h-full max-w-7xl relative">
                  {/* Display Video or Image based on currentImageIndex */}
                  <div className="w-full h-full flex items-center justify-center">
                    {/* Video (index 0 if video exists) */}
                    {imageGalleryProperty.video_url && currentImageIndex === 0 ? (
                      <div className="w-full h-full flex flex-col items-center justify-center">
                        <div className="mb-4 bg-red-600 text-white px-6 py-3 rounded-full text-lg font-bold flex items-center space-x-2">
                          <span>🎬</span>
                          <span>Property Video Tour</span>
                        </div>
                        <video
                          controls
                          autoPlay
                          className="max-w-full max-h-full rounded-lg shadow-2xl"
                          style={{ maxHeight: '80vh', maxWidth: '90vw' }}
                          src={imageGalleryProperty.video_url}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      /* Image Display */
                      <img
                        src={imageGalleryProperty.images[imageGalleryProperty.video_url ? currentImageIndex - 1 : currentImageIndex]}
                        alt={`${imageGalleryProperty.society_name} - Image ${currentImageIndex}`}
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        style={{ maxHeight: '85vh' }}
                      />
                    )}
                  </div>

                  {/* Navigation Arrows */}
                  {(imageGalleryProperty.images.length > 1 || imageGalleryProperty.video_url) && (
                    <>
                      {/* Left Arrow */}
                      <button
                        onClick={() => {
                          const totalItems = imageGalleryProperty.images.length + (imageGalleryProperty.video_url ? 1 : 0);
                          setCurrentImageIndex((prev) => prev === 0 ? totalItems - 1 : prev - 1);
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl transition shadow-xl"
                      >
                        ←
                      </button>

                      {/* Right Arrow */}
                      <button
                        onClick={() => {
                          const totalItems = imageGalleryProperty.images.length + (imageGalleryProperty.video_url ? 1 : 0);
                          setCurrentImageIndex((prev) => prev === totalItems - 1 ? 0 : prev + 1);
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl transition shadow-xl"
                      >
                        →
                      </button>
                    </>
                  )}

                  {/* Counter */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black bg-opacity-70 text-white px-6 py-3 rounded-full text-lg font-semibold">
                    {imageGalleryProperty.video_url && currentImageIndex === 0 ? (
                      <span>📹 Video Tour</span>
                    ) : (
                      <span>
                        Photo {imageGalleryProperty.video_url ? currentImageIndex : currentImageIndex + 1} / {imageGalleryProperty.images.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Thumbnail Strip */}
              <div className="flex-shrink-0 bg-black bg-opacity-80 p-4 border-t border-gray-700">
                <div className="max-w-7xl mx-auto">
                  <div className="flex items-center space-x-3 overflow-x-auto pb-2">
                    {/* Video Thumbnail */}
                    {imageGalleryProperty.video_url && (
                      <button
                        onClick={() => setCurrentImageIndex(0)}
                        className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-3 transition-all ${
                          currentImageIndex === 0
                            ? 'border-red-500 ring-2 ring-red-400 scale-110' 
                            : 'border-gray-600 opacity-60 hover:opacity-100 hover:scale-105'
                        }`}
                      >
                        <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-800 flex flex-col items-center justify-center">
                          <span className="text-3xl mb-1">🎬</span>
                          <span className="text-white text-xs font-bold">VIDEO</span>
                        </div>
                      </button>
                    )}

                    {/* Image Thumbnails */}
                    {imageGalleryProperty.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(imageGalleryProperty.video_url ? idx + 1 : idx)}
                        className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border-3 transition-all ${
                          (imageGalleryProperty.video_url && currentImageIndex === idx + 1) || (!imageGalleryProperty.video_url && currentImageIndex === idx)
                            ? 'border-brand-500 ring-2 ring-brand-400 scale-110' 
                            : 'border-gray-600 opacity-60 hover:opacity-100 hover:scale-105'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Action Bar */}
              <div className="flex-shrink-0 bg-black bg-opacity-90 p-4 border-t border-gray-700">
                <div className="max-w-7xl mx-auto flex justify-center">
                  <button
                    onClick={() => {
                      setImageGalleryProperty(null);
                      setSelectedProperty(imageGalleryProperty);
                    }}
                    className="bg-brand-500 hover:bg-brand-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition shadow-lg"
                  >
                    📞 Contact Us About This Property
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ADMIN LOGIN PAGE ====================

export default BuyPropertyPage;
